// eslint-disable-next-line no-restricted-imports -- This is the only place where useMediaQuery is needed
import { useMediaQuery, useTheme } from "@mui/material";
import { XXXL_BREAKPOINT } from "@styles/common-styles";
import { getIsHoverSupported } from "@utils/ui-utils";

export interface MediaQueryListProps {
  /** Whether the screen is extra small breakpoint. Less than 600px */
  isScreenXs: boolean;

  /** Whether the screen is small breakpoint. 600px to less than 900px */
  isScreenSm: boolean;

  /** Whether the screen is small or any smaller breakpoints. Less than 900px */
  isScreenSmOrSmaller: boolean;

  /** Whether the screen is small or any larger breakpoints. 600px and up */
  isScreenSmOrLarger: boolean;

  /** Whether the screen is medium breakpoint. 900px to less than 1200px */
  isScreenMd: boolean;

  /** Whether the screen is medium or any smaller breakpoints. Less than 1200px */
  isScreenMdOrSmaller: boolean;

  /** Whether the screen is medium or any larger breakpoints. 900px and up */
  isScreenMdOrLarger: boolean;

  /** Whether the screen is large breakpoint. 1200px to less than 1536px */
  isScreenLg: boolean;

  /** Whether the screen is large or any smaller breakpoints. Less than 1536px */
  isScreenLargeOrSmaller: boolean;

  /** Whether the screen is large or any larger breakpoints. 1200px and up */
  isScreenLgOrLarger: boolean;

  /** Whether the screen is extra large breakpoint. 1536px to less than 1920px */
  isScreenXl: boolean;

  /** Whether the screen is extra large or any smaller breakpoints. Less than 1920px */
  isScreenXlOrSmaller: boolean;

  /** Whether the screen is extra large or any larger breakpoints. 1536px and up */
  isScreenXlOrLarger: boolean;

  /** Whether the screen is extra extra large breakpoint. 1920px to less than 2560px */
  isScreenXXL: boolean;

  /** Whether the screen is extra extra large or any smaller breakpoints. Less than 2560px */
  isScreenXXLOrSmaller: boolean;

  /** Whether the screen is extra extra large or any larger breakpoints. 1920px and up */
  isScreenXXLOrLarger: boolean;

  /** Whether the screen is extra extra extra large breakpoint. 2560px and up */
  isScreenXXXL: boolean;

  /**
   * Whether the media supports hovering or not. For example, a touch screen media without mouse cannot hover
   * More conditions can be added from https://stackoverflow.com/a/52854585
   */
  isHoverSupported: boolean;
}

/** Hook to get the current media query state */
export function useMediaQueryList(): MediaQueryListProps {
  const theme = useTheme();

  // XS
  const isScreenXs = useMediaQuery(theme.breakpoints.only("xs"));
  
  // SM
  const isScreenSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isScreenSmOrSmaller = useMediaQuery(theme.breakpoints.down("md"));
  const isScreenSmOrLarger = useMediaQuery(theme.breakpoints.up("sm"));

  // MD
  const isScreenMd = useMediaQuery(theme.breakpoints.only("md"));
  const isScreenMdOrSmaller = useMediaQuery(theme.breakpoints.down("lg"));
  const isScreenMdOrLarger = useMediaQuery(theme.breakpoints.up("md"));

  // LG
  const isScreenLg = useMediaQuery(theme.breakpoints.only("lg"));
  const isScreenLargeOrSmaller = useMediaQuery(theme.breakpoints.down("xl"));
  const isScreenLgOrLarger = useMediaQuery(theme.breakpoints.up("lg"));

  // XL
  const isScreenXl = useMediaQuery(theme.breakpoints.only("xl"));
  const isScreenXlOrSmaller = useMediaQuery(theme.breakpoints.down("xxl"));
  const isScreenXlOrLarger = useMediaQuery(theme.breakpoints.up("xl"));

  // XXL
  const isScreenXXL = useMediaQuery(theme.breakpoints.only("xxl"));
  // Need to use `or isScreenXXL` because "down" excludes the provided breakpoint
  const isScreenXXLOrSmaller = useMediaQuery(theme.breakpoints.down("xxl")) || isScreenXXL;
  const isScreenXXLOrLarger = useMediaQuery(theme.breakpoints.up("xxl"));

  // XXXL
  const isScreenXXXL = useMediaQuery(`(min-width: ${XXXL_BREAKPOINT}px)`);

  // Other tools
  const isHoverSupported = getIsHoverSupported();

  return {
    isScreenXs,
    isScreenSm,
    isScreenSmOrSmaller,
    isScreenSmOrLarger,
    isScreenMd,
    isScreenMdOrSmaller,
    isScreenMdOrLarger,
    isScreenLg,
    isScreenLargeOrSmaller,
    isScreenLgOrLarger,
    isScreenXl,
    isScreenXlOrSmaller,
    isScreenXlOrLarger,
    isScreenXXL,
    isScreenXXLOrSmaller,
    isScreenXXLOrLarger,
    isScreenXXXL,
    isHoverSupported,
  };
}
